import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Typography,
  Alert,
  Tooltip,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useCan } from '@refinedev/core';
import { adminApi } from '@/services/api';
import { PerBuildConfigGuide } from './PerBuildConfigGuide';
import type { ConfigVersion, CreateConfigVersion } from '@/types';

const { Text, Title, Paragraph } = Typography;

const DOTTED_NUMERIC = /^\d+(\.\d+)*$/;

/** Column header with a short explanation, so the table is self-describing. */
const colTitle = (label: string, help: string) => (
  <Tooltip title={help}>
    <span>
      {label} <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
    </span>
  </Tooltip>
);

const compareVersions = (a: string, b: string): number => {
  const pa = a.split('.').map((n) => parseInt(n, 10) || 0);
  const pb = b.split('.').map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d < 0 ? -1 : 1;
  }
  return 0;
};

/** Tri-state: inherit the global value, or force it on/off for this version. */
type TriState = 'inherit' | 'on' | 'off';

const toTri = (v: boolean | null | undefined): TriState =>
  v === null || v === undefined ? 'inherit' : v ? 'on' : 'off';

const fromTri = (v: TriState | undefined): boolean | undefined =>
  v === 'on' ? true : v === 'off' ? false : undefined;

const TRI_OPTIONS = [
  { value: 'inherit', label: 'Inherit global' },
  { value: 'on', label: 'On' },
  { value: 'off', label: 'Off' },
];

interface FormValues {
  version: string;
  description?: string;
  minAppVersion?: string;
  latestAppVersion?: string;
  updateUrl?: string;
  forceUpdate?: TriState;
  softUpdate?: TriState;
  /** Client toggles, edited as a tri-state per known feature.* key. */
  flags?: Record<string, TriState>;
}

export const ConfigVersionsTab: React.FC = () => {
  const [rows, setRows] = useState<ConfigVersion[]>([]);
  const [flagKeys, setFlagKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [available, setAvailable] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ConfigVersion | null>(null);
  const [saving, setSaving] = useState(false);
  const [cloning, setCloning] = useState<ConfigVersion | null>(null);
  const [cloneLabel, setCloneLabel] = useState('');
  const [form] = Form.useForm<FormValues>();

  const { data: canEdit } = useCan({ resource: 'config', action: 'edit' });
  const editable = canEdit?.can !== false;

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cvRes, flagsRes] = await Promise.all([
        adminApi.getConfigVersions(),
        adminApi.getFeatureFlags(),
      ]);
      setRows([...(cvRes.data ?? [])].sort((a, b) => compareVersions(a.version, b.version)));
      // Only client-side toggles can be overridden per config version; the
      // server reads every other flag from the global map, so overriding one
      // would show a feature the server then refuses.
      const all = Object.keys((flagsRes.data as Record<string, boolean>) ?? {});
      setFlagKeys(all.filter((k) => k.startsWith('feature.')).sort());
      setAvailable(true);
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        setAvailable(false);
      } else {
        message.error('Failed to load config versions');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  const apiError = (error: unknown, fallback: string) => {
    const detail = (error as { response?: { data?: { message?: string | string[] } } })?.response
      ?.data?.message;
    message.error(Array.isArray(detail) ? detail.join(', ') : (detail ?? fallback));
  };

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      forceUpdate: 'inherit',
      softUpdate: 'inherit',
      flags: Object.fromEntries(flagKeys.map((k) => [k, 'inherit' as TriState])),
    });
    setModalOpen(true);
  };

  const openEdit = (row: ConfigVersion) => {
    setEditing(row);
    form.setFieldsValue({
      version: row.version,
      description: row.description ?? undefined,
      minAppVersion: row.minAppVersion ?? undefined,
      latestAppVersion: row.latestAppVersion ?? undefined,
      updateUrl: row.updateUrl ?? undefined,
      forceUpdate: toTri(row.forceUpdate),
      softUpdate: toTri(row.softUpdate),
      flags: Object.fromEntries(
        flagKeys.map((k) => [
          k,
          k in (row.featureFlagOverrides ?? {})
            ? row.featureFlagOverrides[k]
              ? 'on'
              : 'off'
            : 'inherit',
        ])
      ) as Record<string, TriState>,
    });
    setModalOpen(true);
  };

  const handleSubmit = async (values: FormValues) => {
    setSaving(true);
    try {
      const overrides: Record<string, boolean> = {};
      for (const [key, state] of Object.entries(values.flags ?? {})) {
        const resolved = fromTri(state);
        if (resolved !== undefined) overrides[key] = resolved;
      }
      const payload: CreateConfigVersion = {
        version: values.version,
        description: values.description || undefined,
        minAppVersion: values.minAppVersion || undefined,
        latestAppVersion: values.latestAppVersion || undefined,
        updateUrl: values.updateUrl || undefined,
        forceUpdate: fromTri(values.forceUpdate),
        softUpdate: fromTri(values.softUpdate),
        featureFlagOverrides: overrides,
      };
      if (editing) {
        await adminApi.updateConfigVersion(editing.id, payload);
        message.success(`Config version ${payload.version} updated`);
      } else {
        await adminApi.createConfigVersion(payload);
        message.success(`Config version ${payload.version} created`);
      }
      setModalOpen(false);
      await fetchAll();
    } catch (error) {
      apiError(error, 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: ConfigVersion) => {
    try {
      await adminApi.deleteConfigVersion(row.id);
      message.success(`Config version ${row.version} deleted`);
      await fetchAll();
    } catch (error) {
      apiError(error, 'Delete failed');
    }
  };

  const handleClone = async () => {
    if (!cloning) return;
    try {
      await adminApi.cloneConfigVersion(cloning.id, { version: cloneLabel });
      message.success(`Cloned ${cloning.version} to ${cloneLabel}`);
      setCloning(null);
      setCloneLabel('');
      await fetchAll();
    } catch (error) {
      apiError(error, 'Clone failed');
    }
  };

  const overrideCell = (value: string | boolean | null | undefined) => {
    if (value === null || value === undefined) return <Text type="secondary">Global</Text>;
    if (typeof value === 'boolean') {
      return <Tag color={value ? 'red' : 'default'}>{value ? 'ON' : 'OFF'}</Tag>;
    }
    return <Text code>{value}</Text>;
  };

  const columns = [
    {
      title: colTitle(
        'Version',
        'The label clients receive as configVersion. Changing it makes those clients refetch their config.'
      ),
      dataIndex: 'version',
      key: 'version',
      render: (v: string) => <Tag color="blue">{v}</Tag>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (d: string | null) => d || <Text type="secondary">—</Text>,
    },
    {
      title: colTitle(
        'Used by',
        'How many app versions point at this config version. Zero means nothing is serving it yet.'
      ),
      dataIndex: 'usedByCount',
      key: 'usedByCount',
      render: (n: number | undefined) =>
        n ? (
          `${n} app version${n === 1 ? '' : 's'}`
        ) : (
          <Tooltip title="No app version points at this config version, so nothing serves it yet.">
            <Tag>unused</Tag>
          </Tooltip>
        ),
    },
    {
      title: colTitle(
        'Min App',
        'Below this version the client shows a forced update. "Global" means it uses the value on the Force Update tab.'
      ),
      dataIndex: 'minAppVersion',
      key: 'minAppVersion',
      render: overrideCell,
    },
    {
      title: colTitle(
        'Latest App',
        'The newest version available. Used for the optional update prompt.'
      ),
      dataIndex: 'latestAppVersion',
      key: 'latestAppVersion',
      render: overrideCell,
    },
    {
      title: colTitle(
        'Force',
        'Blocks play until the player updates. "Global" inherits the Force Update tab.'
      ),
      dataIndex: 'forceUpdate',
      key: 'forceUpdate',
      render: overrideCell,
    },
    {
      title: colTitle(
        'Soft',
        'Shows a dismissible update prompt. "Global" inherits the Force Update tab.'
      ),
      dataIndex: 'softUpdate',
      key: 'softUpdate',
      render: overrideCell,
    },
    {
      title: colTitle(
        'Flag overrides',
        'Client feature toggles changed for builds on this config version. Only feature.* keys can be overridden. Takes effect on the next app launch.'
      ),
      key: 'flags',
      render: (_: unknown, row: ConfigVersion) => {
        const entries = Object.entries(row.featureFlagOverrides ?? {});
        if (entries.length === 0) return <Text type="secondary">—</Text>;
        return (
          <Space direction="vertical" size={2}>
            {entries.map(([k, v]) => (
              <Tag key={k} color={v ? 'green' : 'volcano'}>
                {k} {v ? 'on' : 'off'}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, row: ConfigVersion) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            disabled={!editable}
            onClick={() => openEdit(row)}
          >
            Edit
          </Button>
          <Tooltip title="Copy every override into a new version">
            <Button
              size="small"
              icon={<CopyOutlined />}
              disabled={!editable}
              onClick={() => {
                setCloning(row);
                setCloneLabel('');
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this config version?"
            description="Only possible when no app version points at it."
            onConfirm={() => handleDelete(row)}
            disabled={!editable}
          >
            <Button size="small" danger icon={<DeleteOutlined />} disabled={!editable} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (!available) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Config version API not available"
        description="This backend does not expose /admin/config/config-versions yet."
      />
    );
  }

  return (
    <Card loading={loading}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <PerBuildConfigGuide current="config-versions" configVersionCount={rows.length} />

        <div>
          <Title level={5}>Config Versions</Title>
          <Text type="secondary">
            A config version is the bundle of settings an app version receives. Every field left as
            Inherit falls back to the global values on the Force Update tab, so a new config version
            changes nothing until you override something and point a build at it.
          </Text>
        </div>

        {rows.length === 0 && !loading && (
          <Alert
            type="info"
            showIcon
            message="No config versions yet"
            description="Start here. Create one, map an app version to it on the next tab, then set it on the content you want limited to that build. Until all three are done, every client keeps getting the global configuration."
          />
        )}

        <div>
          <Tooltip title={editable ? '' : 'You have read-only access to config'}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreate}
              disabled={!editable}
            >
              New Config Version
            </Button>
          </Tooltip>
        </div>

        <Table
          rowKey="id"
          dataSource={rows}
          columns={columns}
          pagination={false}
          size="small"
          scroll={{ x: 'max-content' }}
        />
      </Space>

      <Modal
        title={editing ? `Edit config version ${editing.version}` : 'New config version'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={saving}
        okText={editing ? 'Save' : 'Create'}
        width={620}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="version"
            label="Version label"
            rules={[
              { required: true, message: 'Required' },
              { pattern: DOTTED_NUMERIC, message: 'Use dotted numbers, e.g. 2.0.0' },
            ]}
            extra="Clients receive this as configVersion and refetch their config when it changes."
          >
            <Input placeholder="2.0.0" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <Input placeholder="e.g. Blitz Showdown release" maxLength={255} />
          </Form.Item>

          <Divider orientation="left" plain>
            Update prompts
          </Divider>
          <Paragraph type="secondary" style={{ marginTop: 0 }}>
            Leave these on Inherit to use the global Force Update settings.
          </Paragraph>

          <Form.Item
            name="minAppVersion"
            label="Min App Version"
            rules={[{ pattern: DOTTED_NUMERIC, message: 'Use dotted numbers' }]}
          >
            <Input placeholder="Inherit global" />
          </Form.Item>

          <Form.Item
            name="latestAppVersion"
            label="Latest App Version"
            rules={[{ pattern: DOTTED_NUMERIC, message: 'Use dotted numbers' }]}
          >
            <Input placeholder="Inherit global" />
          </Form.Item>

          <Form.Item name="forceUpdate" label="Force Update">
            <Select options={TRI_OPTIONS} />
          </Form.Item>

          <Form.Item name="softUpdate" label="Soft Update">
            <Select options={TRI_OPTIONS} />
          </Form.Item>

          <Form.Item name="updateUrl" label="Update URL">
            <Input placeholder="Inherit global" maxLength={512} />
          </Form.Item>

          <Divider orientation="left" plain>
            Client feature toggles
          </Divider>
          {flagKeys.length === 0 ? (
            <Alert
              type="info"
              showIcon
              message="No client toggles registered"
              description="Only feature.* flags can be overridden here. Every other flag also gates server endpoints, so it has to stay the same for all builds."
            />
          ) : (
            <>
              <Paragraph type="secondary" style={{ marginTop: 0 }}>
                Takes effect on the player&apos;s next app launch.
              </Paragraph>
              {flagKeys.map((key) => (
                <Form.Item key={key} name={['flags', key]} label={<Text code>{key}</Text>}>
                  <Select options={TRI_OPTIONS} />
                </Form.Item>
              ))}
            </>
          )}
        </Form>
      </Modal>

      <Modal
        title={cloning ? `Clone ${cloning.version}` : ''}
        open={!!cloning}
        onCancel={() => setCloning(null)}
        onOk={handleClone}
        okText="Clone"
        okButtonProps={{ disabled: !DOTTED_NUMERIC.test(cloneLabel) }}
        destroyOnHidden
      >
        <Paragraph type="secondary">
          Copies every override into a new config version. Nothing points at the copy until you map
          an app version to it.
        </Paragraph>
        <Input
          placeholder="2.1.0"
          value={cloneLabel}
          onChange={(e) => setCloneLabel(e.target.value)}
        />
      </Modal>
    </Card>
  );
};

export default ConfigVersionsTab;
